import React from 'react'
import type { Block } from '../../types'
import { PropertyLabel } from './shared'
import { ChevronUp, ChevronDown, Plus, X, Type, BarChart3, Image, MousePointer, MessageSquare, Code, SlidersHorizontal } from 'lucide-react'

interface StepsPropertiesProps {
  block: Block
  blocks: Block[]
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
}

const BLOCK_ICON: Record<string, React.ElementType> = {
  text: Type,
  chart: BarChart3,
  image: Image,
  button: MousePointer,
  slider: SlidersHorizontal,
  chat_bubble: MessageSquare,
  codeblock: Code,
}

function blockLabel(b: Block): string {
  if (b.label) return b.label
  if (b.content) return b.content.slice(0, 30)
  if (b.type === 'chart') return 'Chart'
  if (b.type === 'image') return 'Image'
  if (b.type === 'button') return b.label || 'Button'
  return b.type
}

export default function StepsProperties({ block, blocks, onUpdateBlock }: StepsPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const steps = (config.steps as string[]) || []
  const stepGroups = (config.stepGroups as Record<string, string[]>) || {}

  const updateConfig = (updates: Record<string, unknown>) => {
    onUpdateBlock(block.id, { config: { ...config, ...updates } })
  }

  const updateSteps = (newSteps: string[], newGroups?: Record<string, string[]>) => {
    const updates: Record<string, unknown> = { steps: newSteps }
    if (newGroups !== undefined) updates.stepGroups = newGroups
    updateConfig(updates)
  }

  const toggleBlockInStep = (stepIdx: number, blockId: string) => {
    const key = String(stepIdx)
    const current = stepGroups[key] || []
    const newGroups = { ...stepGroups }
    if (current.includes(blockId)) {
      newGroups[key] = current.filter((id) => id !== blockId)
    } else {
      // Remove from any other step of this same Steps block first
      for (const [k, ids] of Object.entries(newGroups)) {
        newGroups[k] = ids.filter((id) => id !== blockId)
      }
      newGroups[key] = [...(newGroups[key] || []).filter((id) => id !== blockId), blockId]
    }
    updateConfig({ stepGroups: newGroups })
  }

  // Available blocks: exclude this steps block and other steps blocks
  const availableBlocks = blocks.filter((b) => b.id !== block.id && b.type !== 'steps')

  // Which step (if any) owns a block in THIS steps block
  const blockOwner = new Map<string, number>()
  for (const [key, ids] of Object.entries(stepGroups)) {
    for (const id of ids) {
      blockOwner.set(id, Number(key))
    }
  }

  const variant = (config.variant as string) || 'dots'

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <PropertyLabel>Style</PropertyLabel>
        <div className="grid grid-cols-2 gap-1.5">
          {([
            { value: 'dots', label: 'Dots' },
            { value: 'numbered', label: 'Numbered' },
            { value: 'timeline', label: 'Timeline' },
            { value: 'progress-bar', label: 'Progress' },
          ] as const).map((opt) => (
            <button
              key={opt.value}
              onClick={() => updateConfig({ variant: opt.value })}
              className={`py-2 px-2 rounded-lg border text-xs font-medium transition-all ${
                variant === opt.value
                  ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <PropertyLabel>Steps</PropertyLabel>
      {steps.map((step, i) => (
        <div key={i} className="space-y-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-400 w-5 shrink-0 text-center">{i + 1}</span>
            <input
              type="text"
              value={step}
              onChange={(e) => {
                const newSteps = [...steps]
                newSteps[i] = e.target.value
                updateSteps(newSteps)
              }}
              className="flex-1 bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none"
            />
            <button
              onClick={() => {
                if (i > 0) {
                  const newSteps = [...steps];
                  [newSteps[i - 1], newSteps[i]] = [newSteps[i], newSteps[i - 1]]
                  // Swap stepGroups keys
                  const newGroups = { ...stepGroups }
                  const a = String(i - 1)
                  const b_key = String(i)
                  const tmp = newGroups[a]
                  newGroups[a] = newGroups[b_key] || []
                  newGroups[b_key] = tmp || []
                  updateSteps(newSteps, newGroups)
                }
              }}
              disabled={i === 0}
              className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
            >
              <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
            </button>
            <button
              onClick={() => {
                if (i < steps.length - 1) {
                  const newSteps = [...steps];
                  [newSteps[i], newSteps[i + 1]] = [newSteps[i + 1], newSteps[i]]
                  const newGroups = { ...stepGroups }
                  const a = String(i)
                  const b_key = String(i + 1)
                  const tmp = newGroups[a]
                  newGroups[a] = newGroups[b_key] || []
                  newGroups[b_key] = tmp || []
                  updateSteps(newSteps, newGroups)
                }
              }}
              disabled={i === steps.length - 1}
              className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
            >
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
            <button
              onClick={() => {
                const newSteps = [...steps]
                newSteps.splice(i, 1)
                // Re-index stepGroups: remove key i, shift keys > i down
                const newGroups: Record<string, string[]> = {}
                for (const [key, ids] of Object.entries(stepGroups)) {
                  const k = Number(key)
                  if (k < i) newGroups[key] = ids
                  else if (k > i) newGroups[String(k - 1)] = ids
                  // k === i → dropped
                }
                updateSteps(newSteps, newGroups)
              }}
              className="p-1 hover:bg-slate-100 rounded"
            >
              <X className="h-3.5 w-3.5 text-slate-400" />
            </button>
          </div>

          {/* Linked blocks for this step */}
          {availableBlocks.length > 0 && (
            <div className="ml-5 pl-3 border-l-2 border-slate-100 space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Linked blocks</span>
              {availableBlocks.map((ab) => {
                const owner = blockOwner.get(ab.id)
                const isInThisStep = owner === i
                const isInOtherStep = owner !== undefined && owner !== i
                const Icon = BLOCK_ICON[ab.type] || Type
                return (
                  <label
                    key={ab.id}
                    className={`flex items-center gap-2 py-1 px-1.5 rounded text-xs cursor-pointer transition-colors ${
                      isInThisStep ? 'bg-[#5048e5]/5' : isInOtherStep ? 'opacity-40' : 'hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isInThisStep}
                      disabled={isInOtherStep}
                      onChange={() => toggleBlockInStep(i, ab.id)}
                      className="rounded border-slate-300 text-[#5048e5] focus:ring-[#5048e5] h-3.5 w-3.5"
                    />
                    <Icon className="h-3 w-3 text-slate-400 shrink-0" />
                    <span className="truncate text-slate-600">
                      {blockLabel(ab)}
                    </span>
                    {isInOtherStep && (
                      <span className="text-[9px] text-slate-400 ml-auto shrink-0">Step {owner! + 1}</span>
                    )}
                  </label>
                )
              })}
            </div>
          )}
        </div>
      ))}
      <button
        onClick={() => updateSteps([...steps, `Step ${steps.length + 1}`])}
        className="flex items-center gap-1 text-xs text-[#5048e5] hover:text-[#5048e5]/80 font-medium"
      >
        <Plus className="h-3.5 w-3.5" /> Add Step
      </button>

      {/* Variable info */}
      {block.variable && (
        <div className="pt-3 border-t border-slate-100">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Variable</span>
          <p className="text-xs text-slate-500 mt-1 font-mono">@{block.variable}</p>
        </div>
      )}
    </div>
  )
}
